'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import { animate, useInView } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';
import type { PageFabioAffiliateSchema } from '@/sanity/pages/PageFabioAffiliateTemplate';

import waveDots from '@/public/fabio-affiliate/wave-dots.png';
import ScrollingBanner from '../global/ScrollingBanner';

function CountUpValue({
  value,
  className,
}: {
  value: string;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });

  const parsed = useMemo(() => {
    const match = value.match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!match) return null;
    const [, prefix, numStr, suffix] = match;
    const hasComma = numStr.includes(',');
    const cleanNum = numStr.replace(/,/g, '');
    const decimalIdx = cleanNum.indexOf('.');
    const decimals = decimalIdx >= 0 ? cleanNum.length - decimalIdx - 1 : 0;
    const target = parseFloat(cleanNum);
    if (isNaN(target)) return null;
    return { prefix, suffix, hasComma, decimals, target };
  }, [value]);

  const format = (n: number) => {
    if (!parsed) return value;
    const { prefix, suffix, hasComma, decimals } = parsed;
    const num = hasComma
      ? n.toLocaleString('en-US', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : n.toFixed(decimals);
    return `${prefix}${num}${suffix}`;
  };

  const [display, setDisplay] = useState(() => (parsed ? format(0) : value));

  useEffect(() => {
    if (!inView || !parsed) return;
    const controls = animate(0, parsed.target, {
      duration: 2.2,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(format(latest)),
    });
    return () => controls.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, parsed]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}

export default function VipDealSection({
  data,
}: {
  data: PageFabioAffiliateSchema;
}) {
  if (
    !data?.vipDealTitle?.length &&
    !data?.vipDealItems?.length &&
    !data?.vipDealCTA
  )
    return null;

  return (
    <section className="">
      <div className="flex relative flex-col items-center mx-auto text-center lg:px-0 gap-[3rem] bg-[#001B14] overflow-clip px-[2rem] pb-[12rem] pt-[6.5rem] lg:py-[16.5rem] lg:h-[111.125rem]">
        <Image
          src={waveDots}
          alt=""
          className="absolute !w-full !h-full inset-0 object-cover pointer-events-none"
        />
        {data?.heroCalloutText && (
          <p className="m-0 italic leading-none text-white font-blacker text-[2rem]">
            {data.heroCalloutText}
          </p>
        )}
        {data?.vipDealTitle?.length > 0 && (
          <div className="richtext richtext-center prose-h2:text-white   prose-h2:font-[500] prose-h2:leading-[115%] prose-h2:tracking-[-0.25rem] lg:prose-h2:tracking-[-0.4rem]">
            <PortableText
              value={data.vipDealTitle}
              components={richTextComponent}
            />
          </div>
        )}

        {data?.vipDealItems?.length > 0 && (
          <div className="flex flex-col justify-center items-center w-full lg:flex-row gap-[2rem] lg:gap-[6rem]">
            {data.vipDealItems.map((item, i) => {
              const holeCount = 7;
              const holePercents = Array.from(
                { length: holeCount },
                (_, n) => ((n + 0.5) / holeCount) * 100,
              );
              const maskGradients = holePercents
                .map(
                  (p) =>
                    `radial-gradient(circle 2rem at 0 ${p}%, transparent 99%, #000 100%)`,
                )
                .join(',');
              return (
                <div
                  key={`${item?.label ?? 'item'}-${i}`}
                  className="relative w-[min(66.25rem,100%)]"
                >
                  <div
                    className="flex flex-col items-center bg-white/[2%] gap-[0.5rem] backdrop-blur-[0.25rem] border-white/[4%] border w-full py-[4rem]"
                    style={{
                      WebkitMaskImage: maskGradients,
                      maskImage: maskGradients,
                      WebkitMaskComposite: 'source-in',
                      maskComposite: 'intersect',
                    }}
                  >
                    <div className="flex flex-col">
                      <div className="flex items-baseline">
                        {item?.value && (
                          <CountUpValue
                            value={item.value}
                            className="leading-none text-transparent bg-clip-text bg-[linear-gradient(180deg,#D6EEE7_35.39%,rgba(255,255,255,0.00)_166.01%)] text-[18.75rem] lg:text-[31.25rem] font-[500] font-geist lg:tracking-[-1.875rem] pr-[1.5rem] tracking-[-1.125rem] tabular-nums"
                          />
                        )}
                        {item?.unit && (
                          <span className="text-white/40 font-[500] tracking-[-0.675rem] font-geist text-[10rem] lg:text-[13.5rem]">
                            {item.unit}
                          </span>
                        )}
                      </div>
                      {item?.label && (
                        <span className="w-full leading-none text-[#D6EEE7] font-[400] font-geist text-start text-[2.25rem] pl-[2rem] lg:pl-[4rem] lg:text-[2.5rem]">
                          {item.label}
                        </span>
                      )}
                    </div>
                  </div>
                  {holePercents.map((p) => (
                    <div
                      key={p}
                      className="absolute left-0 rounded-full border -translate-x-1/2 -translate-y-1/2 pointer-events-none w-[4rem] h-[4rem] border-white/5"
                      style={{
                        top: `${p}%`,
                        clipPath: 'inset(0 0 0 50%)',
                      }}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        )}

        {data?.vipDealCTA && <CTAButton data={data.vipDealCTA} />}

        <ScrollingBanner
          theme="dark"
          className="bg-transparent border-y-0 absolute bottom-0 left-0 w-full"
          words={data?.scrollingWords?.words}
          wordsI18n={data?.scrollingWords?.wordsI18n}
        />
      </div>
    </section>
  );
}
